/* eslint class-methods-use-this: 0 */
import React, { Component } from 'react';
import moment from 'moment-timezone';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
} from '@cargomatic/ui';
import SelectReasonCodes from 'components/modules/marketplace-config/SelectReasonCodes';
import DatePicker from 'components/v2/DatePicker';
import { getTimezones } from 'utils/Timezones';
import { getTimezone } from 'helpers/stops';
import { withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import CargomaticSelect from 'components/v2/select';
import { getShipment } from 'utils/ShipmentWebAPIUtils';
import { processDateTimeChange } from 'utils/formatters/Date';
import TimePicker from 'components/v2/TimePicker';
import SoftAlertHoldNotification from 'components/modules/shipments/stops/SoftAlertHoldNotification';

function getArrivalDefaultDate(stop) {
  if (!stop) return '';
  const { window_start } = stop;
  return window_start && moment().isAfter(window_start)
    ? moment(window_start).tz(getTimezone(stop))
    : moment().tz(getTimezone(stop));
}

class StopArrivedModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      shipment: props.shipment,
      stop: props.stop,
      meta: {
        date: getArrivalDefaultDate(props.stop),
        timezone: props.stop ? getTimezone(props.stop) : null,
        time: props.stop
          ? getArrivalDefaultDate(props.stop).format('hh:mm A')
          : '',
        reason: null,
        comment: '',
        commentRequired: false,
        timeChanged: false,
        dateChanged: false,
      },
      requireCodes: false,
      usingFutureDate: false,
      validDateEntered: false,
      validateTimeEntered: false,
      isDateInvalid: false,
    };
  }

  fetchShipment = async (shipmentId, stopId) => {
    try {
      const result = await getShipment(shipmentId);
      const shipment = result?.entity?.item;
      const stop = shipment?.stops.find((s) => s._id === stopId);
      const { meta } = this.state;
      meta.timezone = getTimezone(stop);
      meta.date = getArrivalDefaultDate(stop);
      meta.time = stop ? getArrivalDefaultDate(stop).format('hh:mm A') : '';
      this.setState({
        shipment,
        stop,
        meta,
      });
    } catch (e) {
      console.error(e);
    }
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.shipment && nextProps.stop) {
      this.setState({
        shipment: nextProps.shipment,
        stop: nextProps.stop,
      });
    } else {
      this.fetchShipment(nextProps.shipmentId, nextProps.stopId);
    }
  }

  componentDidMount() {
    const { shipmentId, stopId } = this.props;
    if (!this.state.shipment || !this.state.stop)
      this.fetchShipment(shipmentId, stopId);
  }

  setCodesRequired = (requireCodes) => {
    this.setState({ requireCodes });
  };

  handleDateChange = (value) => {
    this.setState((prevState) => {
      const result = processDateTimeChange({
        date: value,
        time: prevState.meta.time,
        timezone: prevState.meta.timezone,
      });
      const {
        date,
        isDateInvalid,
        validTimeEntered,
        validDateEntered,
        usingFutureDate,
      } = result;
      const meta = {
        ...prevState.meta,
        date,
        dateChanged: true,
      };

      return {
        meta,
        validDateEntered,
        validTimeEntered,
        isDateInvalid,
        usingFutureDate,
      };
    });
  };

  handleTimezoneChange = (zone) => {
    const timezone = zone.value;
    this.setState((prevState) => {
      const result = processDateTimeChange({
        date: prevState.meta.date,
        time: prevState.meta.time,
        timezone,
      });
      const {
        date,
        isDateInvalid,
        validTimeEntered,
        validDateEntered,
        usingFutureDate,
      } = result;
      const meta = {
        ...prevState.meta,
        date,
        timezone,
      };

      return {
        meta,
        validDateEntered,
        validTimeEntered,
        isDateInvalid,
        usingFutureDate,
      };
    });
  };

  handleTimeChange = (e) => {
    const {
      target: { value: time },
    } = e;
    this.setState((prevState) => {
      const result = processDateTimeChange({
        date: prevState.meta.date,
        time,
        timezone: prevState.meta.timezone,
      });
      const {
        date,
        isDateInvalid,
        validTimeEntered,
        validDateEntered,
        usingFutureDate,
      } = result;
      const meta = {
        ...prevState.meta,
        date,
        time,
        timeChanged: true,
      };

      return {
        meta,
        validDateEntered,
        validTimeEntered,
        isDateInvalid,
        usingFutureDate,
      };
    });
  };

  handleReasonSelect = (item) => {
    const { meta } = this.state;
    meta.reason = item;
    this.setState({ meta });
  };

  handleCommentInput = (comment) => {
    const { meta } = this.state;
    meta.comment = comment;
    if (comment.length) {
      meta.commentRequired = false;
    }
    this.setState({ meta });
  };

  handleCommentRequired = (value) => {
    const { t } = this.props;
    const { meta } = this.state;
    meta.commentRequired = value;
    if (value) {
      meta.error = t('CUSTOM_REASON_CODE_REQUIRE_COMMENT');
    }
    this.setState({ meta });
  };

  handleComplete = () => {
    const { shipment, stop, meta } = this.state;
    meta.date = moment(meta.date).toISOString();

    this.props.onStopArrived(shipment, stop, 'arrived', meta);
    this.setState({ meta: {} }, () => {
      this.props.onHide();
    });
  };

  handleHide = () => {
    this.setState({ meta: {} }, () => {
      this.props.onHide();
    });
  };

  isValid = () => {
    const { meta, requireCodes, usingFutureDate, isDateInvalid } = this.state;
    const { reason, comment, commentRequired, dateChanged, timeChanged } = meta;
    if (usingFutureDate) {
      return false;
    }

    if (!dateChanged || !timeChanged) {
      return false;
    }

    if (isDateInvalid) {
      return false;
    }

    if (requireCodes) {
      if (!reason) {
        return false;
      }
      if (commentRequired && (!comment || !comment.length)) {
        return false;
      }
    }
    return true;
  };

  renderSoftAlertForHolds() {
    const { shipment } = this.state;
    if (!shipment || !shipment.holds) return null;
    return <SoftAlertHoldNotification shipment={shipment} />;
  }

  render() {
    const { t } = this.props;
    const { shipment, stop, meta, usingFutureDate } = this.state;

    const handleOpenChange = (open) => {
      if (!open) {
        this.handleHide();
      }
    };

    return (
      <Dialog open={this.props.show} onOpenChange={handleOpenChange}>
        <DialogContent
          data-testid="CargomaticModal-9ae6fef4"
          className="max-w-2xl max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle>{t('ARRIVAL_INFO')}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {!shipment && <p>{t('LOADING')}</p>}
            {stop && shipment && (
              <>
                {this.renderSoftAlertForHolds()}
                <div className="space-y-2">
                  <Label>{t('DATE_AND_TIME_STOP_MESSAGE')}</Label>
                  <div className="flex flex-wrap items-center gap-2">
                    <DatePicker
                      dataTestId="DatePicker-0a335a49"
                      placeholderText={t('ARRIVAL_DATE')}
                      selected={meta.dateChanged && meta.date}
                      onChange={(selectedDate) =>
                        this.handleDateChange(selectedDate)
                      }
                      onChangeRaw={(e) => this.handleDateChange(e.target.value)}
                      isClearable
                    />
                    <TimePicker
                      dataTestId="TimePicker-5da94a5c"
                      value={meta.timeChanged && meta.time}
                      onChange={this.handleTimeChange}
                    />
                    <CargomaticSelect
                      dataTestId="CargomaticSelect-819223a2"
                      hideLabel
                      onSelect={this.handleTimezoneChange}
                      items={getTimezones()}
                      value={meta.timezone}
                      valueKey="value"
                      textKey="name"
                    />
                  </div>
                </div>
                {usingFutureDate && (
                  <div className="text-sm text-destructive">
                    {t('COMPLETION_DATE_FUTURE_WARNING')}
                  </div>
                )}
                <SelectReasonCodes
                  shipment={shipment}
                  category="stop_arrival"
                  updateComment={this.handleCommentInput}
                  selectReasonCode={this.handleReasonSelect}
                  updateCommentRequired={this.handleCommentRequired}
                  setCodesRequired={this.setCodesRequired}
                />
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={this.handleHide}>
              {titleize(t('CANCEL'))}
            </Button>
            <Button onClick={this.handleComplete} disabled={!this.isValid()}>
              {titleize(t('SAVE'))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
}

export default withTranslation()(StopArrivedModal);
